import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchActiveCoachRuntimeContext } from "@/lib/coachRuntimeContext";
import { appQueryKeys } from "@/lib/queryKeys";

export function useCoachRuntimeContext(params: {
  coachId?: string;
  leaderId?: string | null;
  enabled?: boolean;
}) {
  const { coachId, leaderId, enabled = true } = params;
  const queryClient = useQueryClient();

  const runtimeContextQuery = useQuery({
    queryKey: appQueryKeys.coachMemoryTranscriptContext(coachId, leaderId ?? undefined),
    queryFn: async () => {
      const result = await fetchActiveCoachRuntimeContext({ coachId: coachId!, leaderId });
      queryClient.setQueryData(appQueryKeys.coachLensActive(coachId), result.coachLens);
      queryClient.setQueryData(appQueryKeys.coachTwinActive(coachId), result.coachTwin);
      return result;
    },
    enabled: Boolean(enabled && coachId),
  });

  return useMemo(
    () => ({
      coachLens: runtimeContextQuery.data?.coachLens ?? null,
      coachTwin: runtimeContextQuery.data?.coachTwin ?? null,
      coachMemory: runtimeContextQuery.data?.coachMemory ?? null,
      isFetched: runtimeContextQuery.isFetched,
      isLoading: runtimeContextQuery.isLoading,
      isFetching: runtimeContextQuery.isFetching,
      isError: runtimeContextQuery.isError,
    }),
    [
      runtimeContextQuery.data,
      runtimeContextQuery.isFetched,
      runtimeContextQuery.isFetching,
      runtimeContextQuery.isLoading,
      runtimeContextQuery.isError,
    ],
  );
}
